import React, { useState, useMemo } from 'react';
import { Form, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { FaBoxOpen, FaTags, FaGraduationCap, FaClipboardCheck, FaInfoCircle } from 'react-icons/fa';

// Generic components and hooks
import GenericCrudPage from '../components/GenericCrudPage';
import { useDegrees } from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function ComponentMasterPage() {
  const [selectedDegree, setSelectedDegree] = useState('');
  const { degrees = [], loading: degreesLoading } = useDegrees();

  // Enhanced configuration with better API response handling
  const componentMasterConfig = useMemo(() => ({
    title: 'Component',
    entityName: 'Component',
    baseUrl: `${BASE_URL}/masters`,
    endpoints: {
      fetch: '/getComponentMaster',
      save: '/createComponentMaster',
      update: '/updateComponentMaster',
      delete: '/deleteComponentMaster',
    },
    fetchMethod: 'POST',
    deleteMethod: 'POST',
    fetchPayload: {
      degree: Number(selectedDegree) || 0,
    },
    deletePayload: {
      user: 1
    },
    initialFormState: {
      exComponentName: '',
      degree: selectedDegree,
      maxMarks: '',
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.exComponentName?.trim()) {
        errors.exComponentName = 'Component name is required.';
      }
      if (!form.degree) {
        errors.degree = 'Degree is required.';
      }
      if (!form.maxMarks || form.maxMarks <= 0) {
        errors.maxMarks = 'Valid max marks are required.';
      }
      return errors;
    },
  }), [selectedDegree]);

  const formFields = [
    {
      name: 'exComponentName',
      label: 'Component Name',
      icon: FaTags,
      required: true,
      placeholder: 'Enter component name'
    },
    {
      name: 'degree',
      label: 'Degree',
      icon: FaGraduationCap,
      as: 'select',
      required: true,
      options: [
        { value: '', label: '-- Select Degree --' },
        ...degrees.map(d => ({ value: d.id, label: d.exName })),
      ]
    },
    {
      name: 'maxMarks',
      label: 'Max Marks',
      icon: FaClipboardCheck,
      type: 'number',
      required: true,
      placeholder: 'Enter maximum marks'
    }
  ];
  
  // Table columns configured for your specific API response structure
  const tableColumns = [
    { 
      key: 'exComponentName', 
      label: 'Component Name', 
      sortable: true,
      render: (item) => {
        return item.exComponentName || '-';
      }
    },
    { 
      key: 'degreename', 
      label: 'Degree', 
      sortable: true,
      render: (item) => {
        return item.degreename || '-';
      }
    },
    { 
      key: 'maxMarks', 
      label: 'Max Marks', 
      sortable: true, 
      align: 'center',
      render: (item) => {
        return `${item.maxMarks || 0} marks`;
      }
    },
  ];
  
  const transformPayload = (form, editId, userId) => ({
    exComponentName: form.exComponentName?.trim(),
    degree: Number(form.degree),
    maxMarks: Number(form.maxMarks),
    user: userId,
    ...(editId && { id: editId }),
  });
  
  // Transform data for editing - matching your API response structure
  const transformToForm = (item) => {
    return {
      exComponentName: item.exComponentName || '',
      degree: String(item.degreeid || selectedDegree), // Use degreeid from API response
      maxMarks: String(item.maxMarks || ''),
    };
  };

  // View fields configured for your API response structure
  const getViewFields = (item) => {
    if (!item) return [];

    return [
      { 
        label: 'Component ID', 
        value: item.id, 
        icon: FaInfoCircle 
      },
      { 
        label: 'Component Name', 
        value: item.exComponentName || '-', 
        icon: FaTags,
        fullWidth: false 
      },
      { 
        label: 'Degree', 
        value: item.degreename || '-', 
        icon: FaGraduationCap 
      },
      { 
        label: 'Max Marks', 
        value: `${item.maxMarks || 0} marks`, 
        icon: FaClipboardCheck 
      },
    ];
  };

  // CSV headers matching your API response structure
  const csvHeaders = [
    { key: 'id', label: 'ID' },
    { key: 'exComponentName', label: 'Component Name' },
    { key: 'degreename', label: 'Degree' },
    { key: 'maxMarks', label: 'Max Marks' },
    { key: 'isactive', label: 'Status' },
  ];

  const handleDegreeChange = (e) => {
    const newDegree = e.target.value;
    setSelectedDegree(newDegree);
    console.log('Selected degree changed to:', newDegree);
  };

  return (
    <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
     

      <div className="mb-4">
        <h2 className="fw-bolder page-title mb-2">Component Master</h2>
        <p className="text-muted">Manage components for different degree programs</p>
      </div>
      
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={3}>
              <div className="d-flex align-items-center">
                <FaGraduationCap className="text-primary me-2" />
                <span className="fw-bold text-muted">Filter by Degree:</span>
              </div>
            </Col>
            <Col md={9}>
              <Form.Select
                value={selectedDegree}
                onChange={handleDegreeChange}
                disabled={degreesLoading}
                className="form-select-lg"
              >
                <option value="">
                  {degreesLoading ? 'Loading degrees...' : '-- Select a Degree to View Components --'}
                </option>
                {degrees?.map((degree) => (
                  <option key={degree.id} value={degree.id}>
                    {degree.exName}
                  </option>
                )) || []}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {selectedDegree ? (
        <GenericCrudPage
          title="Component"
          config={componentMasterConfig}
          formFields={formFields}
          tableColumns={tableColumns}
          transformPayload={transformPayload}
          transformToForm={transformToForm}
          getViewFields={getViewFields}
          emptyIcon={FaBoxOpen}
          searchPlaceholder="Search components by name, degree, or marks..."
          csvFilename={`components_${degrees?.find(d => d.id == selectedDegree)?.exName?.replace(/\s+/g, '_') || 'unknown'}_${new Date().toISOString().split('T')[0]}.csv`}
          csvHeaders={csvHeaders}
        />
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaGraduationCap size={64} className="text-muted mb-4 opacity-50" />
            <h4 className="text-muted mb-3">Select a Degree Program</h4>
            <p className="text-muted mb-0">
              Please select a degree from the dropdown above to view and manage its components.
              <br />
              <small>Components define the evaluation criteria for each degree program.</small>
            </p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}